import { Helmet } from "react-helmet-async";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback, useRef } from "react"
import ConversationList from "./ConversationList"
import ChatWindow from "./ChatWindow"
import { getMessageSenderName } from "../../api/ChannelMessage"
import CustomerInfo from "./CustomerInfo"
import EmptyState from "./EmptyState"
import Layout from "../../LayoutChat"
import { useLocation, useParams, useNavigate } from "react-router-dom"
import { getChannelCustomers, getChannelCustomersByGroup, updateChannelCustomer, getChannelCustomerDetail, markReadChannelCustomer, ChannelCustomerItem, ChannelCustomerDetail, ChannelCustomerAdvancedFilters } from "../../api/ChannelCustomer"
import { getQuickFilterParams, QuickFilterKey } from "./QuickFilterRail"
import { getChannelConnectionById, ChannelConnectionDetailResponse } from "../../api/ChannelConnection"
import { getChannelGroupById } from "../../api/ChannelGroup"
import { isRealtimeEnabled, subscribeChannelMessages, COL_CONNECTION_ID } from "../../services/realtime"
import { REALTIME_FALLBACK_POLL } from "env"

export default function RoomChat() {
    const { t, i18n } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { search } = useLocation();
    const navigate = useNavigate();

    // Ghi customer_id vào URL để khi tải lại trang vẫn mở đúng khách đang chat
    const syncCustomerIdToUrl = (customerId: number | null) => {
        const params = new URLSearchParams(search);
        if (customerId) params.set("customer_id", String(customerId));
        else params.delete("customer_id");
        const qs = params.toString();
        navigate(`/chat/${id}${qs ? `?${qs}` : ""}`, { replace: true });
    };

    type Conversation = {
        id: number
        name: string
        avatar: string
        time: string
        labels?: string[]
        lastMessage?: string
        // Nhân viên đã gửi tin cuối (rỗng nếu khách gửi hoặc gửi ngoài CRM). Tách riêng khỏi
        // lastMessage vì lastMessage còn dùng để dò khách theo nội dung tin trên Business Suite
        lastMessageStaff?: string
        // Hướng tin nhắn cuối ("inbound" = khách gửi, chưa được trả lời)
        direction?: string | null
        unreadCount?: number
        externalUserId?: string
        connectionName?: string
        isPriority?: boolean
        isPinned?: boolean
        type?: string // 'chat' | 'comment'
        customer?: { id: number;[key: string]: any } | null
        profileLink?: string | null // link trang cá nhân của khách trên kênh
    }

    const [activeChat, setActiveChat] = useState<Conversation | null>(null)
    const [showInfo, setShowInfo] = useState(false)
    const [conversationData, setConversationData] = useState<Conversation[]>([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [channelName, setChannelName] = useState("")
    // ID trang Facebook của kênh -> dựng link hộp thư Business Suite cho khách nhắn tin
    const [connectionExternalId, setConnectionExternalId] = useState("")
    // Đếm số request cập nhật đang chạy, không dùng cờ boolean vì bấm chồng nhau
    // thì request xong trước sẽ mở khoá sớm trong khi request sau còn đang chạy
    const updatingRef = useRef(0)
    // Thẻ người dùng vừa bấm nhưng API chưa xác nhận xong. Mọi lần refresh danh sách
    // đều phải đắp đè giá trị này lên dữ liệu server, nếu không một response cũ đang
    // bay về sẽ ghi đè lựa chọn vừa bấm và thẻ bị nhảy lại như cũ.
    // savedAt = thời điểm API xác nhận lưu xong; còn null nghĩa là đang chờ.
    const pendingLabelsRef = useRef<Map<number, { labels: string[]; savedAt: number | null }>>(new Map())
    // Đếm số lần bấm theo từng khách, để bỏ qua response về trễ của lần bấm cũ
    const labelSeqRef = useRef<Map<number, number>>(new Map())

    // Thẻ chăm sóc của khách ĐANG MỞ, lấy từ API chi tiết chứ không tham chiếu danh sách
    // bên trái: khách nằm ngoài phân trang sẽ không có trong danh sách, lúc đó tham chiếu
    // sẽ ra rỗng và bấm một thẻ là xoá sạch các thẻ còn lại của khách.
    const [activeLabels, setActiveLabels] = useState<string[]>([])
    const activeLabelsSeqRef = useRef(0)

    const applyPendingLabels = useCallback((list: Conversation[]): Conversation[] => {
        if (pendingLabelsRef.current.size === 0) return list
        return list.map(chat => {
            const pending = pendingLabelsRef.current.get(chat.id)
            return pending ? { ...chat, labels: pending.labels } : chat
        })
    }, [])

    // Chỉ ngừng đắp đè khi đã có một lần refresh BẮT ĐẦU sau lúc lưu xong — lúc đó
    // dữ liệu server chắc chắn đã mới. Refresh bắt đầu trước đó vẫn là dữ liệu cũ.
    const releaseSyncedLabels = useCallback((refreshStartedAt: number) => {
        pendingLabelsRef.current.forEach((entry, chatId) => {
            if (entry.savedAt !== null && entry.savedAt < refreshStartedAt) {
                pendingLabelsRef.current.delete(chatId)
            }
        })
    }, [])
    const autoOpenedRef = useRef(false)
    const loadedOnceRef = useRef(false)      // đã load xong danh sách lần đầu chưa
    const fallbackTriedRef = useRef(false)   // đã thử gọi API lấy riêng khách chưa
    const pageRef = useRef(1)
    const searchRef = useRef("")
    const careStatusRef = useRef<number | undefined>(undefined)
    const typeRef = useRef<string | undefined>(undefined) // lọc theo loại: 'chat' | 'comment' | undefined (tất cả)
    // Tham số của bộ lọc nhanh đang chọn ở thanh icon (is_unread / unanswered / is_priority_contact...)
    const quickFilterRef = useRef<ChannelCustomerAdvancedFilters>({})
    const loadingMoreRef = useRef(false)
    const [selectedCareStatusId, setSelectedCareStatusId] = useState<number | undefined>(undefined)
    const [selectedType, setSelectedType] = useState<string | undefined>(undefined)
    const PAGE_SIZE = 20

    const queryParams = new URLSearchParams(search);
    const preferredCustomerId = Number(queryParams.get("customer_id"));
    // group=true: id trên URL là ID nhóm kênh, load khách hàng theo nhóm
    const isGroup = queryParams.get("group") === "true";

    const formatTime = (isoString: string) => {
        if (!isoString) return "";
        try {
            const date = new Date(isoString);
            return date.toLocaleString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return isoString;
        }
    }

    // Nguồn sự thật là quan hệ nhiều-nhiều care_statuses; customer_care_status_id
    // chỉ là trạng thái chính, dùng làm dự phòng cho bản ghi cũ chưa có pivot.
    const mapCareStatusesToLabels = (source: { care_statuses?: { id: number }[]; customer_care_status_id?: number | null }): string[] => {
        if (source.care_statuses?.length) {
            return source.care_statuses.map(s => String(s.id))
        }
        return source.customer_care_status_id ? [String(source.customer_care_status_id)] : []
    }

    const mapCustomerToConversation = (item: ChannelCustomerItem): Conversation => ({
        id: item.id,
        name: item.display_name || t('chats.unknown_customer'),
        avatar: item.avatar || item.avatar_url || "",
        time: formatTime(item.last_seen_at),
        lastMessage: item.last_message_text || "",
        lastMessageStaff: item.last_message?.direction === "inbound"
            ? ""
            : item.last_message_staff_name || (item.last_message ? getMessageSenderName(item.last_message) : ""),
        direction: item.direction ?? null,
        unreadCount: item.unread_count || 0,
        externalUserId: item.external_user_id,
        connectionName: item.connection?.display_name || "",
        labels: mapCareStatusesToLabels(item),
        isPriority: !!item.is_priority_contact,
        isPinned: !!item.is_ping,
        type: item.type,
        customer: item.customer ?? null,
        profileLink: item.profile_link ?? null,
    })

    // Gọi API danh sách khách theo trang: nhóm kênh (group=true) hoặc kết nối kênh
    const fetchPage = useCallback((page: number, limit: number) => {
        // Bộ lọc nhanh (chưa đọc / chưa phản hồi / ưu tiên...) gửi kèm dạng tham số API
        const quick = quickFilterRef.current;
        const common = {
            search: searchRef.current || undefined,
            customer_care_status_id: careStatusRef.current,
            type: typeRef.current,
            // Mục "chưa phản hồi" tự kèm sort đợi lâu nhất; còn lại giữ sort mặc định
            ...(quick.sortRaw ? { sortRaw: quick.sortRaw } : { sort: ['last_seen_at,desc'] }),
            ...quick,
            page,
            limit,
        };
        if (isGroup) {
            return getChannelCustomersByGroup(Number(id), common);
        }
        return getChannelCustomers({ channel_connection_id: Number(id), ...common });
    }, [id, isGroup]);

    const fetchCustomers = useCallback(async (search?: string, showLoading = true) => {
        if (!id) return;
        if (updatingRef.current) return;
        searchRef.current = search || "";
        pageRef.current = 1;
        if (showLoading) setLoading(true);
        const res = await fetchPage(1, PAGE_SIZE);
        if (res.status && res.data) {
            setConversationData(applyPendingLabels(res.data.items.map(mapCustomerToConversation)));
            setHasMore(res.data.items.length < res.data.total);
        }
        loadedOnceRef.current = true;
        if (showLoading) setLoading(false);
    }, [id, fetchPage, applyPendingLabels]);

    // Refetch yên lặng toàn bộ các trang đang mở (cập nhật unread, last_message, thứ tự...)
    const refreshList = useCallback(async () => {
        if (!id) return;
        if (updatingRef.current || loadingMoreRef.current) return;
        const startedAt = Date.now();
        const res = await fetchPage(1, PAGE_SIZE * pageRef.current);
        if (res.status && res.data) {
            releaseSyncedLabels(startedAt);
            setConversationData(applyPendingLabels(res.data.items.map(mapCustomerToConversation)));
            setHasMore(res.data.items.length < res.data.total);
        }
    }, [id, fetchPage, applyPendingLabels, releaseSyncedLabels]);

    // Làm mới danh sách khi vừa khôi phục số tin chưa đọc từ ChatWindow
    useEffect(() => {
        const onRestored = () => refreshList();
        window.addEventListener("conversation-unread-restored", onRestored);
        return () => window.removeEventListener("conversation-unread-restored", onRestored);
    }, [refreshList]);

    // Đồng bộ avatar khi cập nhật từ ChatWindow
    useEffect(() => {
        const onAvatar = (e: any) => {
            const { id: cid, avatar } = e?.detail || {};
            if (!cid) return;
            setConversationData(prev => prev.map(c => c.id === cid ? { ...c, avatar } : c));
            setActiveChat(prev => prev && prev.id === cid ? { ...prev, avatar } : prev);
        };
        window.addEventListener("channel-customer-avatar-updated", onAvatar);
        return () => window.removeEventListener("channel-customer-avatar-updated", onAvatar);
    }, []);

    const handleFilterByCareStatus = useCallback((careStatusId?: number) => {
        careStatusRef.current = careStatusId;
        setSelectedCareStatusId(careStatusId);
        fetchCustomers(searchRef.current);
    }, [fetchCustomers]);

    // Lọc theo loại hội thoại: 'chat' | 'comment' | undefined (tất cả)
    const handleFilterByType = useCallback((type?: string) => {
        typeRef.current = type;
        setSelectedType(type);
        fetchCustomers(searchRef.current);
    }, [fetchCustomers]);

    // Lọc nhanh bằng thanh icon: đổi tham số API rồi nạp lại danh sách từ trang 1
    const handleQuickFilterChange = useCallback((key: QuickFilterKey) => {
        const { type, ...advanced } = getQuickFilterParams(key) || {};
        quickFilterRef.current = advanced;
        typeRef.current = type;
        setSelectedType(type);
        fetchCustomers(searchRef.current);
    }, [fetchCustomers]);

    const loadMore = useCallback(async () => {
        if (!id) return;
        if (loadingMoreRef.current) return;
        if (updatingRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        const nextPage = pageRef.current + 1;
        const res = await fetchPage(nextPage, PAGE_SIZE);
        if (res.status && res.data) {
            const mapped = res.data.items.map(mapCustomerToConversation);
            pageRef.current = nextPage;
            setConversationData(prev => {
                const existingIds = new Set(prev.map(c => c.id));
                const merged = [...prev, ...mapped.filter(c => !existingIds.has(c.id))];
                setHasMore(merged.length < res.data!.total);
                return merged;
            });
        }
        loadingMoreRef.current = false;
        setLoadingMore(false);
    }, [id, fetchPage]);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    useEffect(() => {
        if (!id) return;
        let active = true;
        // Đưa tên kênh/nhóm hiện tại lên header global
        const apply = (name: string) => {
            if (!active) return;
            setChannelName(name);
            window.dispatchEvent(new CustomEvent("chat-channel-name", { detail: name }));
        };
        if (isGroup) {
            // Chế độ gộp trang: hiển thị tên nhóm thay cho tên kênh
            getChannelGroupById(Number(id)).then((res) => {
                if (res.status && res.data) apply(res.data.name || "");
            });
        } else {
            getChannelConnectionById(Number(id)).then((res: ChannelConnectionDetailResponse) => {
                if (res.status && res.data) {
                    apply(res.data.display_name || "");
                    if (active) setConnectionExternalId(String((res.data as any).external_id || ""));
                }
            });
        }
        return () => {
            active = false;
            // Rời trang chat -> xóa tên kênh khỏi header
            window.dispatchEvent(new CustomEvent("chat-channel-name", { detail: "" }));
        };
    }, [id, isGroup]);

    useEffect(() => {
        autoOpenedRef.current = false;
        fallbackTriedRef.current = false;
        loadedOnceRef.current = false;
    }, [id, search]);

    // Làm mới danh sách hội thoại khi có tin nhắn/khách mới.
    // Refetch toàn bộ các trang đang mở để cập nhật last_message, unread, thứ tự...
    // - Realtime (kênh đơn): nghe channel_messages của kênh hiện tại -> refetch
    //   (debounce gộp nhiều event), kèm polling chậm 30s dự phòng.
    // - Chế độ gộp nhóm (group=true) hoặc chưa cấu hình Realtime: polling 3s như cũ.
    useEffect(() => {
        if (!id) return;

        // Group mode chưa lọc được theo danh sách kênh con -> giữ polling cũ.
        if (isRealtimeEnabled() && !isGroup) {
            let timer: ReturnType<typeof setTimeout> | null = null;
            const debouncedRefetch = () => {
                if (timer) clearTimeout(timer);
                timer = setTimeout(refreshList, 500);
            };
            const unsub = subscribeChannelMessages({
                channelName: `room-chat-list:conn:${id}`,
                filter: `${COL_CONNECTION_ID}=eq.${id}`,
                onEvent: debouncedRefetch,
            });
            const interval = REALTIME_FALLBACK_POLL ? setInterval(refreshList, 30000) : null;
            return () => {
                unsub();
                if (interval) clearInterval(interval);
                if (timer) clearTimeout(timer);
            };
        }

        const interval = setInterval(refreshList, 3000);
        return () => clearInterval(interval);
    }, [id, isGroup, refreshList]);

    // Một khách có thể mang nhiều trạng thái chăm sóc: click thẻ đang chọn thì bỏ,
    // chưa chọn thì thêm vào — không thay thế các thẻ còn lại.
    const toggleLabel = async (chatId: number, labelKey: string, careStatusId?: number) => {

        // Bấm nhanh nhiều thẻ: lần bấm sau phải cộng dồn lên lần bấm trước, kể cả khi
        // React chưa kịp render lại. Thứ tự lấy mốc: pending -> thẻ của khách đang mở
        // (nguồn từ API chi tiết) -> danh sách bên trái.
        const isActiveChat = activeChat?.id === chatId
        const currentLabels =
            pendingLabelsRef.current.get(chatId)?.labels
            ?? (isActiveChat ? activeLabels : undefined)
            ?? conversationData.find(c => c.id === chatId)?.labels
            ?? []
        const hasLabel = currentLabels.includes(labelKey)
        const nextLabels = hasLabel
            ? currentLabels.filter(key => key !== labelKey)
            : [...currentLabels, labelKey]

        pendingLabelsRef.current.set(chatId, { labels: nextLabels, savedAt: null })
        if (isActiveChat) setActiveLabels(nextLabels)

        setConversationData(prev =>
            prev.map(chat => {

                if (chat.id !== chatId) return chat

                return {
                    ...chat,
                    labels: nextLabels
                }

            })
        )

        // Call API to update care status
        if (careStatusId !== undefined) {
            const seq = (labelSeqRef.current.get(chatId) || 0) + 1
            labelSeqRef.current.set(chatId, seq)

            const nextIds = nextLabels.map(Number).filter(n => !Number.isNaN(n))
            updatingRef.current += 1
            const res = await updateChannelCustomer(chatId, {
                customer_care_status_ids: nextIds,
                // Trạng thái chính, giữ cho chỗ nào còn đọc customer_care_status_id
                customer_care_status_id: nextIds.length > 0 ? nextIds[0] : null
            }).finally(() => { updatingRef.current -= 1 })

            // Đã có lần bấm mới hơn -> response này lỗi thời, để lần mới nhất quyết định
            if (labelSeqRef.current.get(chatId) !== seq) return

            if (res?.status) {
                // Đánh dấu đã lưu, nhưng vẫn giữ đắp đè cho tới khi có refresh mới hơn
                const entry = pendingLabelsRef.current.get(chatId)
                if (entry) entry.savedAt = Date.now()
            } else {
                // Lưu hỏng: bỏ pending và trả thẻ về đúng trạng thái server đang có
                pendingLabelsRef.current.delete(chatId)
                message.error(t("roomchat.update_failed") || "Cập nhật thất bại")
                refreshList()
                // Khách đang mở có thể nằm ngoài danh sách -> lấy lại từ API chi tiết
                if (isActiveChat) {
                    getChannelCustomerDetail(chatId).then((detail) => {
                        if (detail.status && detail.data) {
                            setActiveLabels(mapCareStatusesToLabels(detail.data))
                        }
                    })
                }
            }
        }

    }

    useEffect(() => {

        if (!activeChat) return

        const updated = conversationData.find(c => c.id === activeChat.id)

        if (updated) {
            setActiveChat(updated)
        }

    }, [conversationData])

    // Mở hộp thoại nào thì nạp thẻ của khách đó từ API chi tiết.
    // Nếu đang có thay đổi chưa lưu xong (pending) thì giữ nguyên, tránh response
    // của bản ghi cũ ghi đè lên thẻ người dùng vừa bấm.
    useEffect(() => {
        const chatId = activeChat?.id
        if (!chatId) {
            setActiveLabels([])
            return
        }

        const pending = pendingLabelsRef.current.get(chatId)
        if (pending) {
            setActiveLabels(pending.labels)
            return
        }

        const seq = ++activeLabelsSeqRef.current
        setActiveLabels(activeChat?.labels || [])
        getChannelCustomerDetail(chatId).then((res) => {
            // Đã mở khách khác trong lúc chờ -> bỏ qua kết quả cũ
            if (activeLabelsSeqRef.current !== seq) return
            if (pendingLabelsRef.current.get(chatId)) return
            if (res.status && res.data) {
                setActiveLabels(mapCareStatusesToLabels(res.data))
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeChat?.id])

    // Map chi tiết khách (từ API channel-customer/{id}) sang Conversation để mở hộp thoại
    const mapDetailToConversation = (d: ChannelCustomerDetail): Conversation => ({
        id: d.id,
        name: d.display_name || t('chats.unknown_customer'),
        avatar: d.avatar || d.avatar_url || "",
        time: "",
        // Cần cho việc dò khách trong hộp thư Business Suite (khớp theo tin nhắn cuối)
        lastMessage: d.last_message_text || "",
        direction: (d as any).direction ?? null,
        unreadCount: 0,
        externalUserId: d.external_user_id,
        connectionName: "",
        labels: mapCareStatusesToLabels(d),
        isPriority: false,
        isPinned: !!d.is_ping,
        type: (d as any).type,
        // Giữ liên kết khách CRM nếu đã tạo (để nút hiển thị "Cập nhật" thay vì "Thêm Khách hàng")
        customer: (d as any).customer ?? null,
        profileLink: d.profile_link ?? null,
    })

    useEffect(() => {
        if (autoOpenedRef.current) return;
        if (!Number.isFinite(preferredCustomerId) || preferredCustomerId <= 0) return;

        const matched = conversationData.find((chat) => chat.id === preferredCustomerId);
        if (matched) {
            setActiveChat(matched);
            setShowInfo(false);
            autoOpenedRef.current = true;
            return;
        }

        // Không có trong danh sách đã tải -> gọi API lấy riêng khách rồi mở (1 lần).
        // Chờ list load xong để tránh gọi thừa khi khách thực ra nằm trong trang đầu.
        if (!loadedOnceRef.current || fallbackTriedRef.current) return;
        fallbackTriedRef.current = true;
        getChannelCustomerDetail(preferredCustomerId).then((res) => {
            if (res.status && res.data) {
                const conv = mapDetailToConversation(res.data);
                setConversationData((prev) =>
                    prev.some((c) => c.id === conv.id) ? prev : [conv, ...prev]
                );
                setActiveChat(conv);
                setShowInfo(false);
                autoOpenedRef.current = true;
            }
        });
    }, [conversationData, preferredCustomerId]);

    // Mở hội thoại với khách -> gọi API đánh dấu đã đọc (đưa unread_count về 0).
    // Chạy tập trung theo activeChat.id nên áp dụng cho cả khi chọn thủ công lẫn tự mở từ URL.
    const lastMarkedIdRef = useRef<number | null>(null);
    useEffect(() => {
        const cid = activeChat?.id;
        if (!cid || cid <= 0) return;
        if (lastMarkedIdRef.current === cid) return;
        lastMarkedIdRef.current = cid;
        // Optimistic: đưa badge về 0 ngay cho mượt
        setConversationData(prev =>
            prev.map(c => c.id === cid ? { ...c, unreadCount: 0 } : c)
        );
        markReadChannelCustomer(cid)
            .finally(() => { setTimeout(() => { refreshList() }, 800) });
    }, [activeChat?.id]);

    return (
        <Layout>
            <Helmet>
                <title>{t('page.chat_room')} | Khothi</title>
            </Helmet>

            <div className="flex h-[calc(100vh-64px)] overflow-hidden">

                {/* CỘT 1 */}
                <div
                    className={`
                    w-full md:w-[360px]
                    border-r dark:border-[#2a2a2a]
                    ${activeChat ? "hidden md:block" : "block"}
                `}
                >
                    <ConversationList
                        data={conversationData}
                        loading={loading}
                        loadingMore={loadingMore}
                        hasMore={hasMore}
                        channelName={channelName}
                        selectedCareStatusId={selectedCareStatusId}
                        onFilterByCareStatus={handleFilterByCareStatus}
                        selectedType={selectedType}
                        onFilterByType={handleFilterByType}
                        onQuickFilterChange={handleQuickFilterChange}
                        onLoadMore={loadMore}
                        onSelect={(chat: any) => {
                            setActiveChat(chat)
                            setShowInfo(false)
                            syncCustomerIdToUrl(chat?.id ?? null)
                            // Việc đánh dấu đã đọc được xử lý tập trung ở effect theo activeChat.id
                            // (bao gồm cả trường hợp tự mở lại khách từ URL khi tải lại trang).
                        }}
                        activeChat={activeChat}
                        onSearch={(value: string) => fetchCustomers(value)}
                    />
                </div>

                {/* CỘT 2 */}
                <div
                    className={`
                        flex-1
                        bg-gray-100 dark:bg-[#181818]
                        ${!activeChat ? "hidden md:block" : "block"}
                    `}
                >
                    <div className="flex h-full w-full">
                        {activeChat ? (
                            <ChatWindow
                                user={activeChat}
                                activeLabels={activeLabels}
                                connectionExternalId={connectionExternalId}
                                isGroup={isGroup}
                                toggleLabel={toggleLabel}
                                goBack={() => { setActiveChat(null); syncCustomerIdToUrl(null); }}
                                openInfo={() => setShowInfo(true)}
                            />
                        ) : (
                            <EmptyState />
                        )}
                    </div>
                </div>

                {/* CỘT 3 */}
                <div
                    className={`
                        ${showInfo ? "block" : "hidden xl:block"}
                        xl:static
                        fixed xl:relative
                        right-0 top-0
                        h-full
                        w-full sm:w-[420px]
                        border-l dark:border-[#2a2a2a]
                        bg-white dark:bg-[#141414]
                        z-30 xl:!z-29
                    `}
                >
                    {activeChat && (
                        <CustomerInfo
                            user={activeChat}
                            setShowInfo={setShowInfo}
                            onCustomerCreated={(cust: any) => {
                                // Cập nhật state ngay để DOM phản ánh việc đã tạo khách (bật "Tạo lịch hẹn")
                                const cid = activeChat.id;
                                setActiveChat(prev => prev && prev.id === cid ? { ...prev, customer: cust } : prev);
                                setConversationData(prev => prev.map(c => c.id === cid ? { ...c, customer: cust } : c));
                            }}
                        />
                    )}
                </div>

            </div>

        </Layout>
    )
}